import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PortalBackground from '../components/PortalBackground';

const ProposedOrgChart = () => {
  const [revenue, setRevenue] = useState(0);

  const formatRevenue = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${value.toLocaleString()}`;
  };

  const presetValues = [500000, 1000000, 1500000, 2000000, 2500000, 3000000];

  const navigateRevenue = (direction) => {
    if (direction === 'next') {
      const currentIndex = presetValues.findIndex(val => val > revenue);
      if (currentIndex < presetValues.length) {
        setRevenue(presetValues[currentIndex]);
      }
    } else if (direction === 'prev') {
      if (revenue > 0) {
        const newRevenue = Math.max(0, revenue - 500000);
        setRevenue(newRevenue);
      }
    }
  };

  // Generate tick marks every 500k
  const tickMarks = Array.from({ length: 7 }, (_, i) => i * 500000);

  return (
    <div className="relative min-h-screen p-6">
      <PortalBackground />
      <div className="relative">
      <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-8">Proposed Organization Chart</h1>
      
      {/* Revenue Slider Section */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 mb-8">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-4">
            <button 
              onClick={() => navigateRevenue('prev')}
              className="text-3xl text-red-600 hover:text-red-700 transition-colors"
            >
              <FaChevronLeft />
            </button>
            <div>
              <div className="text-4xl font-bold text-red-600 mb-2">{formatRevenue(revenue)}</div>
              <div className="text-lg text-gray-600">Annual Revenue</div>
            </div>
            <button 
              onClick={() => navigateRevenue('next')}
              className="text-3xl text-red-600 hover:text-red-700 transition-colors"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
        
        <div className="relative pb-12">
          <div className="relative h-4 mb-1">
            {/* Background bar */}
            <div className="absolute w-full h-full bg-gray-200 rounded-lg"></div>
            {/* Progress bar */}
            <div 
              className="absolute h-full bg-red-100 rounded-lg transition-all duration-150"
              style={{ 
                width: `${(revenue / 3000000) * 100}%`,
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #dc2626 10px, #dc2626 20px)',
                animation: 'moveStripes 1s linear infinite'
              }}
            ></div>
            {/* Current position indicator */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
              style={{ 
                left: `${(revenue / 3000000) * 100}%`,
                zIndex: 20 
              }}
            >
              <div className="w-6 h-6 bg-black rounded-full border-2 border-white shadow-lg"></div>
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Current Revenue
              </div>
            </div>
            {/* Hiring indicator circles */}
            <div className="absolute w-full h-full">
              {/* Project Manager indicator at $1M */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
                style={{ left: `${(1000000 / 3000000) * 100}%` }}
              >
                <div className="w-6 h-6 bg-red-600 rounded-full border-2 border-white shadow-lg"></div>
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs py-1 px-2 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  Project Manager Hired
                </div>
              </div>
              {/* Carpenter indicator at $1.5M */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 group"
                style={{ left: `${(1500000 / 3000000) * 100}%` }}
              >
                <div className="w-6 h-6 bg-red-600 rounded-full border-2 border-white shadow-lg"></div>
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs py-1 px-2 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  Carpenter Hired
                </div>
              </div>
            </div>
            {/* Slider input */}
            <input
              type="range"
              min="0"
              max="3000000"
              step="100000"
              value={revenue}
              onChange={(e) => setRevenue(Number(e.target.value))}
              className="absolute w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          
          {/* Labels */}
          <div className="absolute w-full" style={{ top: '24px' }}>
            {tickMarks.map((tick) => (
              <div 
                key={tick} 
                className="absolute transform -translate-x-1/2"
                style={{ left: `${(tick / 3000000) * 100}%` }}
              >
                <div className="text-base font-bold text-gray-700 whitespace-nowrap">
                  {tick >= 1000000 ? `$${(tick/1000000).toFixed(1)}M` : `$${(tick/1000)}K`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add keyframe animation for moving stripes */}
      <style jsx>{`
        @keyframes moveStripes {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 28px 0;
          }
        }
      `}</style>

      {/* Org Chart Section */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6">
        <div className="flex flex-col items-center">
          {/* Owner/Estimates/PD Box - Always shown */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4 w-64 text-center">
            <div className="font-bold text-lg text-red-800">Owner / Estimates / PD</div>
            <div className="text-sm text-red-600">Salary: $40,000</div>
          </div>

          {/* Project Manager Box - Shown at $1M+ */}
          {revenue >= 1000000 && (
            <>
              <div className="w-0.5 h-8 bg-gray-300 mb-4"></div>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4 w-64 text-center">
                <div className="font-bold text-lg text-red-800">Project Manager</div>
                <div className="text-sm text-red-600">Salary: $60,000</div>
              </div>
            </>
          )}

          {/* Carpenter Box - Shown at $1.5M+ */}
          {revenue >= 1500000 && (
            <>
              <div className="w-0.5 h-8 bg-gray-300 mb-4"></div>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 w-64 text-center">
                <div className="font-bold text-lg text-red-800">Carpenter</div>
                <div className="text-sm text-red-600">Salary: $20/Hr</div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProposedOrgChart; 